import { createTheme, css } from '@mui/material';


const colors = {
  Black: '#000000',
  White: '#FFFFFF',
  Lion: {
    main: '#AB854A',
    dark: '#8F6C32',
    light: '#F2EBE2',
  },
  Platinum: '#DFDEDE',
  Vermilion: '#EB3E33',
  DeepSkyBlue: '#00C2FF',
  SunflowerYellow: '#FFBC00',
  GrassGreen: '#1FCE7A',
};


declare module '@mui/material/styles' {
  interface Palette {
    green?: Palette['primary'],
    red?: Palette['primary'],
    platinum?: string,
    deepSkyBlue?: string,
    sunFlowerYellow?: string,
    grassGreen?: string,
  }
  interface PaletteOptions {
    green?: PaletteOptions['primary'],
    red?: PaletteOptions['primary'],
    platinum?: string,
    deepSkyBlue?: string,
    sunFlowerYellow?: string,
    grassGreen?: string,
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    white: true,
    grey: true,
    green: true,
    red: true,
  }
}


export const theme2 = createTheme({
  palette: {
    common: {
      black: colors.Black,
      white: colors.White,
    },
    primary: {
      // Lion
      ...colors.Lion,
      contrastText: colors.White,
    },
    green: {
      main: colors.GrassGreen,
      light: '#5dd892',
      dark: '#00b456',
      contrastText: colors.White,
    },
    red: {
      main: colors.Vermilion,
      light: '#e7504d',
      dark: '#dc3632',
      contrastText: colors.White,
    },
    grey: {
      200: '#F4F4F4',
      300: '#B0B0B0',
      500: colors.Platinum,
      900: '#444444',
    },
    platinum: colors.Platinum,
    deepSkyBlue: colors.DeepSkyBlue,
    sunFlowerYellow: colors.SunflowerYellow,
    grassGreen: colors.GrassGreen,
    white: {
      main: colors.White,
    },
    base: {
      main: colors.White,
    },
    background: {
      default: colors.White,
    },
    success: {
      main: colors.GrassGreen,
    },
    error: {
      main: colors.Vermilion,
    },
  },
  typography: {
    fontFamily: 'CircularStd',
  },
  shape: {
    borderRadius: 12,
  },
  transitions: {
    duration: {
      standard: 300,
    },
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        disableRipple: true,
        variant: 'contained',
      },
      styleOverrides: {
        root: ( { theme } ) => css( {
          boxSizing: 'border-box',
          height: '40px',
          padding: '0 16px',
          fontSize: '14px',
          fontWeight: '900',
          lineHeight: '18px',
          gap: '6px',
          borderRadius: theme.shape.borderRadius,
          textTransform: 'none',
          transition: theme.transitions.create(
            [ 'background-color', 'border-color', 'color' ],
            { duration: theme.transitions.duration.standard, easing: 'ease-out' }
          ),
        } ),
        startIcon: {
          marginRight: 0,
        },
        endIcon: {
          marginLeft: 0,
        },
      },
      variants: [
        {
          props: { color: 'white' },
          style: ( { theme } ) => css( {
            backgroundColor: theme.palette.common?.white,
            color: theme.palette.grey?.[900],
    
            '&:hover': {
              backgroundColor: theme.palette.grey?.[200],
              color: theme.palette.common?.black,
            }
          } )
        },
        {
          props: { color: 'grey' },
          style: ( { theme } ) => css( {
            backgroundColor: theme.palette.grey?.[200],
            color: theme.palette.grey?.[900],
            border: `1px solid ${ theme.palette.grey?.[200] }`,
    
            '&:hover': {
              color: theme.palette.common?.black,
              border: `1px solid ${ theme.palette.grey?.[300] }`,
            },
          } )
        },
        {
          props: { color: 'green' },
          style: ( { theme } ) => css( {
            backgroundColor: theme.palette.green?.main,
            color: theme.palette.common?.white,
    
            '&:hover': {
              backgroundColor: theme.palette.green?.dark,
            }
          } )
        },
        {
          props: { color: 'red' },
          style: ( { theme } ) => css( {
            backgroundColor: theme.palette.red?.main,
            color: theme.palette.common?.white,
    
            '&:hover': {
              backgroundColor: theme.palette.red?.dark,
            }
          } )
        },
      ],
    },
  },
})